import type { BreadcrumbItem } from "@/components/Breadcrumbs";
import type { CatalogPath } from "@/lib/catalog-tree";

export const CATALOG_ROOT = "/catalog";
export const CATALOG_LABEL = "Каталог";

/** `catalogHref(["a", "b"])` → `/catalog/a/b`. */
export function catalogHref(slugs: readonly string[]): string {
  if (slugs.length === 0) return CATALOG_ROOT;
  return `${CATALOG_ROOT}/${slugs.map(encodeURIComponent).join("/")}`;
}

/** Router splat → clean slugs, tolerating empty and trailing segments. */
export function parseCatalogSlugs(splat: string | undefined): string[] {
  if (!splat) return [];
  return splat
    .split("/")
    .filter((segment) => segment.length > 0)
    .map(decodeURIComponent);
}

function chainToBreadcrumbs(
  chain: readonly { id: string; name: string }[],
  trailing: readonly BreadcrumbItem[],
): BreadcrumbItem[] {
  const slugs: string[] = [];
  const items: BreadcrumbItem[] = [{ label: CATALOG_LABEL, href: CATALOG_ROOT }];

  for (const node of chain) {
    slugs.push(node.id);
    items.push({ label: node.name, href: catalogHref(slugs) });
  }

  return [...items, ...trailing];
}

/**
 * "Каталог" plus one entry per ancestor. The last entry keeps its href — `Breadcrumbs`
 * renders the final item as plain text regardless.
 */
export function catalogBreadcrumbs(
  path: CatalogPath,
  trailing: readonly BreadcrumbItem[] = [],
): BreadcrumbItem[] {
  return chainToBreadcrumbs(path, trailing);
}

/** Same, from a flat id/name chain — for pages that have no catalog tree loaded. */
export function catalogBreadcrumbsFromChain(
  chain: readonly { id: string; name: string }[],
  trailing: readonly BreadcrumbItem[] = [],
): BreadcrumbItem[] {
  return chainToBreadcrumbs(chain, trailing);
}
